import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { Photo } from '@entities/photo.class';
import { enregistrerErreurTelemetrie } from '@services/core/telemetrie';

interface ReponseUrlsPhotos {
	expireLe: string;
	urls: { id: string; url: string; urlMiniature?: string }[];
	nonSynchronisees: string[];
}

/** On redemande les URL un peu avant leur expiration réelle. */
const MARGE_EXPIRATION_MS = 30 * 60 * 1000;

/**
 * Chargement DIRECT des photos depuis le stockage S3, hors mode technicien.
 *
 * Jusqu'ici le BE recevait toutes les photos d'un rapport en base64 via l'API dès
 * qu'une seule manquait en local (JSON de ~80 Mo pour 150 photos), converties en blob et
 * réécrites dans IndexedDB : un gros pic mémoire à chaque ouverture. (Les blobs étaient
 * bien libérés en quittant le rapport — revokeRuntimePhotos, ATLAS-151.) Ici on pose une
 * URL présignée dans `photo.objectUrl` : le navigateur charge l'image et la met en cache
 * sur disque. Plus de base64, de blob ni d'IndexedDB.
 *
 * Le vrai poste mémoire, mesuré ensuite, était le décodage des photos 1024 px pour des
 * vignettes : d'où la miniature 512 px, posée par défaut dans `objectUrl`.
 *
 * Le mode technicien n'est PAS concerné : il doit fonctionner hors ligne, donc garder
 * les photos en local.
 *
 * ⚠️ Ces URL expirent : elles ne doivent jamais être enregistrées dans un rendu. Les
 * chemins d'enregistrement passent par `replaceBase64ById` (src → REPLACE_<id>) et les
 * générateurs écrivent directement REPLACE_<id>.
 */
@Injectable({ providedIn: 'root' })
export class PhotoUrlService {
	private http = inject(HttpClient);
	private router = inject(Router);

	/** photoId → URL présignée de la photo entière, en cours de validité. */
	private urls = new Map<string, string>();
	/**
	 * photoId → URL de la miniature 512 px, quand elle existe sur S3.
	 * Mesuré le 24/09 : les photos 1024 px décodées pour des vignettes de 50 px
	 * pesaient ~320 Mo de « cache des images » sur un gros rapport ; une miniature
	 * se décode en 0,75 Mo au lieu de 3 Mo, et se télécharge en ~42 Ko au lieu de ~434.
	 */
	private miniatures = new Map<string, string>();
	/** biId → échéance (ms) des URL de ce rapport. */
	private echeances = new Map<number, number>();
	/** Requêtes en vol, pour ne pas demander deux fois le même rapport en parallèle. */
	private enCours = new Map<number, Promise<string[]>>();

	/** Vrai hors mode technicien (BE, BA, historique, admin). */
	estActif(): boolean {
		return !this.router.url.startsWith('/dashboard_tech');
	}

	/** URL d'affichage : la miniature si elle existe, sinon la photo entière. */
	urlPour(photoId: string): string | undefined {
		return this.miniatures.get(photoId) ?? this.urls.get(photoId);
	}

	/** URL de la photo ENTIÈRE (zoom plein écran), s'il y en a une. */
	urlCompletePour(photoId: string): string | undefined {
		return this.urls.get(photoId);
	}

	/**
	 * Pose l'URL S3 sur chaque photo synchronisée du rapport.
	 * Renvoie les photos restées SANS URL — pas encore envoyées sur S3, ou ajoutées
	 * localement et pas encore synchronisées — que l'appelant charge par l'ancien chemin.
	 */
	async appliquer(biId: number, photos: Photo[]): Promise<Photo[]> {
		if (!photos?.length) return [];

		try {
			await this.chargerRapport(biId);
		} catch (error) {
			// Endpoint indisponible (API pas encore déployée, réseau) : on ne pose rien et
			// tout repart par l'ancien chemin. Dégradé, mais jamais d'image perdue.
			enregistrerErreurTelemetrie(error, 'photo.url.chargement', { 'bi.id': String(biId) });
			return photos.filter(p => !this.aUneUrlUtilisable(p));
		}

		const restantes: Photo[] = [];
		for (const photo of photos) {
			// Photo créée ici et pas encore envoyée : seul le blob local existe.
			if (photo.isSync === false) {
				if (!this.aUneUrlUtilisable(photo)) restantes.push(photo);
				continue;
			}
			if (this.poser(photo)) continue;
			if (!this.aUneUrlUtilisable(photo)) {
				restantes.push(photo);
			}
		}
		return restantes;
	}

	/**
	 * À appeler quand une image S3 ne se charge pas : l'URL a pu expirer (onglet resté
	 * ouvert plus de 36 h) ou la photo vient d'arriver sur S3. Renvoie la nouvelle URL.
	 */
	async rafraichir(biId: number, photo: Photo): Promise<string | undefined> {
		this.echeances.delete(biId);
		try {
			await this.chargerRapport(biId);
		} catch {
			return undefined;
		}
		this.poser(photo);
		return this.urlPour(photo.id);
	}

	/**
	 * Pose sur la photo son URL d'affichage (miniature si disponible — toutes les vues
	 * en petit en profitent sans modification) et son URL complète (zoom). Renvoie vrai
	 * si la photo a une URL S3.
	 */
	private poser(photo: Photo): boolean {
		const complete = this.urls.get(photo.id);
		if (!complete) return false;
		photo.objectUrl = this.miniatures.get(photo.id) ?? complete;
		photo.urlComplete = complete;
		photo._runtimePrepared = true;
		return true;
	}

	/**
	 * Miniature en échec (marquée présente mais absente de S3) : repli sur la photo
	 * entière plutôt qu'une image « indisponible ». Renvoie l'URL complète si le repli
	 * s'applique à `srcEnEchec`.
	 */
	repliSurComplete(photo: Photo, srcEnEchec: string | null | undefined): string | undefined {
		const complete = photo.urlComplete ?? this.urls.get(photo.id);
		if (!complete || !srcEnEchec || srcEnEchec === complete) return undefined;
		if (srcEnEchec !== this.miniatures.get(photo.id)) return undefined;
		// On retire la miniature défaillante pour la suite de la session.
		this.miniatures.delete(photo.id);
		photo.objectUrl = complete;
		return complete;
	}

	/** Vrai si l'URL portée par la photo est une URL S3 (et non un blob local). */
	estUrlDirecte(url: string | undefined | null): boolean {
		return !!url && /^https?:\/\//i.test(url);
	}

	private aUneUrlUtilisable(photo: Photo): boolean {
		const url = photo.objectUrl;
		// Une URL http héritée d'une session précédente a pu expirer : elle ne compte pas.
		return !!url && !this.estUrlDirecte(url);
	}

	/** Charge (ou réutilise) la table des URL du rapport. Renvoie les ids non synchronisés. */
	private chargerRapport(biId: number): Promise<string[]> {
		const echeance = this.echeances.get(biId);
		if (echeance && Date.now() < echeance - MARGE_EXPIRATION_MS) {
			return Promise.resolve([]);
		}
		const existante = this.enCours.get(biId);
		if (existante) return existante;

		const requete = firstValueFrom(this.http.get<ReponseUrlsPhotos>('photo/urls/' + biId))
			.then(reponse => {
				for (const { id, url, urlMiniature } of reponse.urls) {
					this.urls.set(id, url);
					if (urlMiniature) {
						this.miniatures.set(id, urlMiniature);
					}
				}
				this.echeances.set(biId, new Date(reponse.expireLe).getTime());
				return reponse.nonSynchronisees;
			})
			.finally(() => this.enCours.delete(biId));

		this.enCours.set(biId, requete);
		return requete;
	}
}
